// src/api/supabase/agencyEncomienda.js
//
// ── Design decisions ──
// Adaptador real de Supabase para la Encomienda de agencia (Maxi Envíos):
// análisis de lista (foto / PDF / texto / Excel), aplicación de grupos a
// encomienda_packages + encomienda_items, rehidratación, cajas y adjuntos.
// Su gemelo src/api/mock/agencyEncomienda.js exporta EXACTAMENTE los mismos
// nombres y firmas, así que los componentes nunca saben qué backend corre.
//
// · Las funciones LANZAN (Error con mensaje en español) y devuelven valores
//   pelados — nunca { data, error }. La UI atrapa y muestra err.message.
// · La extracción viaja SIEMPRE por supabase.functions.invoke: nunca una URL
//   fija. tenant_id enciende la caché por hash de contenido en la función.
// · Guardia del PDF en el cliente (10 MB, mismo texto que la 413 del servidor)
//   y reducción de fotos a 1 568 px de lado largo ANTES de codificar, para no
//   gastar tokens de visión ni subida.
// · aplicarGrupo escribe UN grupo: paquete (mismas columnas que
//   handleAddPackage) y luego artículos en un solo insert(...).select(), así
//   cada fila vuelve con id real antes de cualquier blur. Si el paquete entra
//   pero los artículos fallan, el error lleva err.packageId para que el
//   reintento use ese paquete como destino existente y nunca lo duplique.
// · Toda eliminación verifica las filas que devuelve .select(): un delete que
//   no borró nada no es un éxito silencioso.
// · crearColaCajas serializa los guardados por caja: si hay uno corriendo,
//   solo se conserva el ÚLTIMO estado en cola (los intermedios se descartan),
//   así dos ciclos borrar-reinsertar de box_packages nunca se pisan.
// · Las columnas de destinatario de la caja salen del paquete principal
//   (el primero), no del destinatario del paso 1.
// · Los adjuntos (recibos, foto del paquete) replican la semántica de
//   PackageCard (bucket package-photos, package_receipts, tope 20, 5 MB) —
//   la copia de PackageCard queda congelada por la regla de no-regresión.
// · Solo se escriben columnas que existen en el esquema verificado; nunca
//   recipient_id.

import { supabase } from '../../supabaseClient';
import { fetchCustomsConfigRow } from '../../utils/customsCalc';
import { insertBox } from '../../utils/insertBox';
import { parseListaTexto } from '../../components/ViajeroEncomienda/shared/parseListaTexto';
import {
  hojasDesdeArrayBuffer,
  parseListaExcelMultiHoja,
} from '../../components/AgencyEncomienda/parseListaExcel';
import {
  mapItemAgency,
  itemAInsert,
  grupoAPaqueteInsert,
  hashLocal,
  recipientDesdePaqueteRow,
  mismoRecipient,
} from '../../components/AgencyEncomienda/agencyMapper';

// ── Constantes ────────────────────────────────────────────────────────────────

export const LIMITE_PDF_BYTES = 10 * 1024 * 1024;
export const ERROR_PDF_GRANDE = 'El PDF es demasiado grande — subí una foto o un PDF más liviano.';
export const LADO_MAX_FOTO = 1568;

export const ITEM_SELECT = '*, customs_catalog ( descripcion, unidad_medida, unit_type )';

const MAX_RECIBOS = 20;
const MAX_ARCHIVO_BYTES = 5 * 1024 * 1024;

const num = (v, def) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const numOrNull = (v) => (v === null || v === undefined || v === '' ? null : num(v, null));

const fallar = (mensaje, extra) => {
  const err = new Error(mensaje);
  if (extra) Object.assign(err, extra);
  return err;
};

// Lanza siempre en español: el mensaje original de Supabase va en err.detail.
const guard = async (mensaje, fn) => {
  try {
    return await fn();
  } catch (err) {
    if (err && err.__es) throw err;
    const e = fallar(mensaje, { detail: err?.message || String(err), __es: true });
    if (err && err.packageId) e.packageId = err.packageId;
    throw e;
  }
};

// ── Archivos ──────────────────────────────────────────────────────────────────

export const archivoABase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || '');
      const coma = res.indexOf(',');
      resolve(coma >= 0 ? res.slice(coma + 1) : res);
    };
    reader.onerror = () => reject(fallar('No se pudo leer el archivo.', { __es: true }));
    reader.readAsDataURL(file);
  });

// Reduce la foto a LADO_MAX_FOTO de lado largo (JPEG q0.85). Devuelve null si
// no hace falta o no se puede (jsdom sin canvas): el llamador cae a la original.
export const reducirImagen = async (file) => {
  try {
    if (typeof createImageBitmap !== 'function') return null;
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const lado = Math.max(width, height);
    if (!lado || lado <= LADO_MAX_FOTO) {
      bitmap.close?.();
      return null;
    }
    const escala = LADO_MAX_FOTO / lado;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * escala);
    canvas.height = Math.round(height * escala);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    const coma = dataUrl.indexOf(',');
    if (coma < 0 || !dataUrl.startsWith('data:image/jpeg')) return null;
    return { base64: dataUrl.slice(coma + 1), mediaType: 'image/jpeg' };
  } catch (_) {
    return null;
  }
};

// ── Normalización del resultado de extracción ─────────────────────────────────

export const normalizarExtraccion = (data, { source = 'ai' } = {}) => {
  const extracted = data?.extracted || {};
  const paquetes = (extracted.paquetes || []).map((g) => ({
    owner_name_raw: g.owner_name_raw ?? '',
    requires_review: Boolean(g.requires_review),
    items: (g.items || []).map((it) => {
      const unit_price = numOrNull(it.unit_price ?? it.valor_unitario);
      return {
        description_raw: String(it.description_raw ?? it.descripcion ?? it.description ?? ''),
        qty: num(it.qty ?? it.cantidad, 1) || 1,
        unit_type: it.unit_type ?? it.unidad_medida ?? null,
        unit_price,
        is_new: it.is_new === undefined || it.is_new === null ? true : Boolean(it.is_new),
        notes: it.notes ?? null,
        requires_review:
          it.requires_review === undefined ? unit_price === null : Boolean(it.requires_review) || unit_price === null,
      };
    }),
  }));
  return {
    extracted: {
      paquetes,
      extraction_notes: extracted.extraction_notes ?? '',
      requires_review: Boolean(extracted.requires_review ?? data?.requires_review ?? true),
    },
    requires_review: Boolean(data?.requires_review ?? true),
    usage: data?.usage ?? null,
    model: data?.model ?? null,
    cached: Boolean(data?.cached),
    content_hash: data?.content_hash ?? null,
    source,
  };
};

// ── Análisis: foto / PDF ──────────────────────────────────────────────────────

export async function extractListaArchivo({ file, tenantId, signal } = {}) {
  if (!file) throw fallar('No se recibió ningún archivo de la lista.', { __es: true });
  const esPdf = file.type === 'application/pdf';
  if (esPdf && file.size > LIMITE_PDF_BYTES) throw fallar(ERROR_PDF_GRANDE, { __es: true });

  let file_base64;
  let file_media_type = file.type || (esPdf ? 'application/pdf' : 'image/jpeg');
  if (!esPdf) {
    const reducida = await reducirImagen(file);
    if (reducida) {
      file_base64 = reducida.base64;
      file_media_type = reducida.mediaType;
    }
  }
  if (!file_base64) file_base64 = await archivoABase64(file);
  if (signal?.aborted) throw fallar('Análisis cancelado.', { __es: true, cancelado: true });

  return guard('No se pudo leer la lista.', async () => {
    const { data, error } = await supabase.functions.invoke('encomienda-list-extract', {
      body: { file_base64, file_media_type, ...(tenantId ? { tenant_id: tenantId } : {}) },
    });
    if (signal?.aborted) throw fallar('Análisis cancelado.', { __es: true, cancelado: true });
    if (error) throw fallar(error.message || 'La extracción falló — intentá de nuevo en unos segundos.', { __es: true });
    if (!data) throw fallar('La extracción no devolvió resultados.', { __es: true });
    if (data.error) throw fallar(data.error, { __es: true, detail: data.detail });
    if (data.extracted?.parse_error) throw fallar('No se pudo leer la lista — intentá con otra foto.', { __es: true });
    const res = normalizarExtraccion(data, { source: esPdf ? 'pdf' : 'ai' });
    // Sin content_hash (p. ej. sin tenant_id la función no cachea): la clave
    // de dedup sale del hash local de los bytes, nunca queda en null.
    return { ...res, content_hash: res.content_hash || hashLocal('archivo:' + file_base64) };
  });
}

// ── Análisis: texto pegado (local, sin red) ───────────────────────────────────

export function analizarTexto(texto) {
  const res = parseListaTexto(texto);
  return { ...res, content_hash: hashLocal('texto:' + String(texto ?? '')), source: 'texto' };
}

// ── Análisis: Excel (local, sin red) ──────────────────────────────────────────

export async function leerExcel(file) {
  if (!file) throw fallar('No se recibió ningún archivo de Excel.', { __es: true });
  const buf = await file.arrayBuffer();
  try {
    return hojasDesdeArrayBuffer(buf);
  } catch (err) {
    throw fallar('No se pudo leer el archivo de Excel — verificá que sea un .xlsx válido.', {
      __es: true,
      detail: err?.message,
    });
  }
}

export function analizarExcelHojas(hojas, { mapeos = {} } = {}) {
  const res = parseListaExcelMultiHoja(hojas, { mapeos });
  const content_hash = hashLocal('excel:' + JSON.stringify(hojas));
  return {
    ...res,
    resultado: { ...res.resultado, content_hash, source: 'excel', cached: false },
  };
}

// ── Manifiesto ────────────────────────────────────────────────────────────────

export async function resolverManifestId(currentManifestId) {
  if (currentManifestId) return currentManifestId;
  return guard('No se pudo resolver el manifiesto abierto.', async () => {
    const { data: openMf, error } = await supabase.from('manifests').select('id').eq('status', 'OPEN').maybeSingle();
    if (error) throw error;
    return openMf?.id || null;
  });
}

// ── Paquetes / artículos ──────────────────────────────────────────────────────

export async function insertarPaquete(row) {
  return guard('No se pudo crear el paquete.', async () => {
    const { data, error } = await supabase.from('encomienda_packages').insert([row]).select().single();
    if (error) throw error;
    if (!data) throw fallar('El paquete no se guardó.', { __es: true });
    return data;
  });
}

export async function insertarItems(rows) {
  if (!rows || rows.length === 0) return [];
  return guard('No se pudieron guardar los artículos.', async () => {
    const { data, error } = await supabase.from('encomienda_items').insert(rows).select();
    if (error) throw error;
    return data || [];
  });
}

export async function aplicarGrupo({ grupo, ctx } = {}) {
  if (!grupo) throw fallar('No hay grupo para aplicar.', { __es: true });
  const { encomiendaId, tenantId, packageNumber, manifestId, defaultRecipient } = ctx || {};
  let packageId = grupo.destino?.tipo === 'existente' ? grupo.destino.packageId : null;
  let packageRow = null;

  // Guardia defensiva: un grupo sin artículos nunca escribe nada (ni paquete
  // nuevo ni artículos en uno existente). El drawer ya lo filtra; acá se
  // garantiza aunque llegue por otra vía.
  if ((grupo.items || []).length === 0) {
    return { packageId, packageRow: null, items: [], omitido: true };
  }

  if (!packageId) {
    packageRow = await insertarPaquete(
      grupoAPaqueteInsert(grupo, { encomiendaId, packageNumber, manifestId, defaultRecipient })
    );
    packageId = packageRow.id;
  }

  try {
    const filas = (grupo.items || []).map((it) => itemAInsert(it, { packageId, tenantId }));
    const insertadas = await insertarItems(filas);
    return { packageId, packageRow, items: insertadas.map(mapItemAgency) };
  } catch (err) {
    // El paquete ya existe: el reintento debe reutilizarlo, nunca duplicarlo.
    err.packageId = packageId;
    throw err;
  }
}

export async function cargarPaquetes({ encomiendaId, tenantId } = {}) {
  return guard('No se pudieron cargar los paquetes.', async () => {
    let q = supabase.from('encomienda_packages').select('*').eq('encomienda_id', encomiendaId);
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data: pkgs, error } = await q.order('package_number', { ascending: true });
    if (error) throw error;
    const out = [];
    for (const row of pkgs || []) {
      const { data: items, error: errItems } = await supabase
        .from('encomienda_items')
        .select(ITEM_SELECT)
        .eq('package_id', row.id)
        .order('created_at', { ascending: true });
      if (errItems) throw errItems;
      out.push({ row, recipient: recipientDesdePaqueteRow(row), items: (items || []).map(mapItemAgency) });
    }
    return out;
  });
}

const unirDireccion = (address, dep, mun) => {
  const sufijo = [dep, mun].filter(Boolean).join(', ');
  if (sufijo && address.toLowerCase().endsWith(sufijo.toLowerCase())) return address;
  return [address, dep, mun].filter(Boolean).join(', ');
};

const recipientAColumnas = (recipient) => {
  const r = recipient || {};
  const address = String(r.address ?? '').trim();
  const dep = String(r.departamento ?? '').trim();
  const mun = String(r.municipio ?? '').trim();
  const phone = String(r.phone ?? '').replace(/\D/g, '');
  return {
    recipient_name: String(r.name ?? '').trim(),
    recipient_phone: phone || null,
    // Idempotente: una dirección hidratada desde la fila ya trae ", depto, muni";
    // no se vuelve a sufijar (si no, cada guardado la alargaría).
    recipient_address: unirDireccion(address, dep, mun),
    recipient_departamento: dep || null,
    recipient_municipio: mun || null,
    delivery_type: r.delivery_type || 'Drop Off',
  };
};

export async function actualizarDestinatario({ packageId, recipient } = {}) {
  return guard('No se pudo actualizar el destinatario del paquete.', async () => {
    const { error } = await supabase.from('encomienda_packages').update(recipientAColumnas(recipient)).eq('id', packageId);
    if (error) throw error;
  });
}

export async function actualizarDestinatarioPorDefecto({ encomiendaId, previo, nuevo } = {}) {
  return guard('No se pudo actualizar el destinatario.', async () => {
    const { data: pkgs, error } = await supabase
      .from('encomienda_packages')
      .select('*')
      .eq('encomienda_id', encomiendaId);
    if (error) throw error;
    const objetivo = (pkgs || []).filter((p) => mismoRecipient(recipientDesdePaqueteRow(p), previo));
    for (const p of objetivo) {
      const { error: errUpd } = await supabase
        .from('encomienda_packages')
        .update(recipientAColumnas(nuevo))
        .eq('id', p.id);
      if (errUpd) throw errUpd;
    }
    return objetivo.length;
  });
}

export async function moverItem({ itemId, targetPackageId } = {}) {
  return guard('No se pudo mover el artículo.', async () => {
    const { error } = await supabase.from('encomienda_items').update({ package_id: targetPackageId }).eq('id', itemId);
    if (error) throw error;
  });
}

export async function eliminarItem(itemId) {
  return guard('No se pudo eliminar el artículo.', async () => {
    const { data, error } = await supabase.from('encomienda_items').delete().eq('id', itemId).select();
    if (error) throw error;
    if (!data || data.length === 0) throw fallar('El artículo ya no existe.', { __es: true });
  });
}

// ── Cajas ─────────────────────────────────────────────────────────────────────

export async function upsertCaja({ caja, ctx, recipient } = {}) {
  const { tenantId, customerId, encomiendaId, manifestId } = ctx || {};
  const weight = parseFloat(caja?.weight_lb);
  if (!weight || weight <= 0) throw fallar('Ingresá el peso de la caja.', { __es: true });
  if (!caja?.dimensions?.trim()) throw fallar('Ingresá las dimensiones de la caja.', { __es: true });
  if (!caja?.packageIds || caja.packageIds.length === 0) {
    throw fallar('Seleccioná al menos un paquete para esta caja.', { __es: true });
  }

  return guard('No se pudo guardar la caja.', async () => {
    const newBoxId = () => `BOX-${customerId}-${Date.now()}`;
    let boxIdStr = caja.dbBoxId || newBoxId();
    const cols = recipientAColumnas(recipient);
    const boxPayload = {
      box_id: boxIdStr,
      tenant_id: tenantId,
      customer_id: customerId,
      encomienda_id: encomiendaId,
      weight_lb: weight,
      dimensions: caja.dimensions.trim(),
      recipient_name: cols.recipient_name,
      recipient_phone: cols.recipient_phone,
      recipient_address: cols.recipient_address,
      departamento: cols.recipient_departamento,
      municipio: cols.recipient_municipio,
      delivery_type: cols.delivery_type,
      manifest_id: manifestId ?? null,
      entry_manifest_id: manifestId ?? null,
    };

    if (caja.dbBoxId) {
      const { error: updErr } = await supabase
        .from('boxes')
        .update(boxPayload)
        .eq('box_id', caja.dbBoxId)
        .eq('tenant_id', tenantId);
      if (updErr) throw updErr;

      const { data: borradas, error: delErr } = await supabase
        .from('box_packages')
        .delete()
        .eq('box_id', caja.dbBoxId)
        .eq('tenant_id', tenantId)
        .select();
      if (delErr) throw delErr;
      if (!Array.isArray(borradas)) throw fallar('No se pudo verificar la limpieza de la caja.', { __es: true });
    } else {
      // The DB trigger numbers the box; a 23505 retry may use a fresh id.
      const insertedBox = await insertBox(supabase, boxPayload, { regenerateId: newBoxId });
      boxIdStr = insertedBox.box_id;
    }

    const bpRows = caja.packageIds.map((pkgId, idx) => ({
      tenant_id: tenantId,
      box_id: boxIdStr,
      client_id: customerId,
      package_type: 'encomienda',
      package_ref_id: pkgId,
      is_primary: idx === 0,
    }));
    const { error: bpErr } = await supabase.from('box_packages').insert(bpRows);
    if (bpErr) throw bpErr;

    return { boxId: boxIdStr };
  });
}

// Cola por caja: una tarea a la vez por clave; mientras corre, solo se
// conserva la ÚLTIMA en espera (las intermedias se descartan).
export function crearColaCajas() {
  const corriendo = new Map(); // localId → Promise
  const pendiente = new Map(); // localId → { tarea, resolvers: [] }

  const correr = (localId, tarea) => {
    const p = (async () => {
      try {
        return await tarea();
      } finally {
        corriendo.delete(localId);
        const sig = pendiente.get(localId);
        if (sig) {
          pendiente.delete(localId);
          const prom = correr(localId, sig.tarea);
          sig.resolvers.forEach(({ resolve, reject }) => prom.then(resolve, reject));
        }
      }
    })();
    corriendo.set(localId, p);
    return p;
  };

  return {
    encolar(localId, tarea) {
      if (!corriendo.has(localId)) return correr(localId, tarea);
      return new Promise((resolve, reject) => {
        const prev = pendiente.get(localId);
        const resolvers = prev ? prev.resolvers : [];
        resolvers.push({ resolve, reject });
        pendiente.set(localId, { tarea, resolvers });
      });
    },
    enCurso(localId) {
      return corriendo.has(localId);
    },
  };
}

export async function eliminarCaja({ boxId, tenantId } = {}) {
  return guard('No se pudo eliminar la caja.', async () => {
    const { data, error } = await supabase.from('boxes').delete().eq('box_id', boxId).eq('tenant_id', tenantId).select();
    if (error) throw error;
    return { eliminado: Array.isArray(data) && data.length > 0 };
  });
}

// ── Adjuntos (misma semántica que PackageCard) ────────────────────────────────

export async function contarRecibos(pkgId) {
  const { count } = await supabase
    .from('package_receipts')
    .select('id', { count: 'exact', head: true })
    .eq('encomienda_package_id', pkgId);
  return count || 0;
}

export async function subirRecibo({ pkgId, encomiendaPublicId, files, receiptCount = 0 } = {}) {
  const lista = Array.from(files || []);
  const errores = [];
  if (lista.length === 0) return { lastUrl: null, count: receiptCount, errores };
  if (receiptCount >= MAX_RECIBOS) {
    return {
      lastUrl: null,
      count: receiptCount,
      errores: ['Máximo 20 recibos por paquete. / Max 20 receipts per package.'],
    };
  }
  const remaining = MAX_RECIBOS - receiptCount;
  const toUpload = lista.slice(0, remaining);
  let lastUrl = null;

  for (const file of toUpload) {
    if (file.size > MAX_ARCHIVO_BYTES) {
      errores.push('Archivo muy grande. Máximo 5MB. / File too large. Max 5MB.');
      continue;
    }
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const fileName = `${encomiendaPublicId || pkgId}-receipt-${Date.now()}.${ext}`;
      await supabase.storage.from('package-photos').upload(fileName, file);
      const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(fileName);
      await supabase.from('package_receipts').insert({
        encomienda_package_id: pkgId,
        receipt_url: urlData.publicUrl,
        uploaded_by: 'staff',
        source: 'staff',
      });
      lastUrl = urlData.publicUrl;
    } catch (err) {
      errores.push(err?.message || 'No se pudo subir el recibo.');
    }
  }

  // Legacy: encomienda_packages.receipt_url apunta al último recibo (no bloquea).
  if (lastUrl) {
    try {
      await supabase.from('encomienda_packages').update({ receipt_url: lastUrl }).eq('id', pkgId);
    } catch (legacyErr) {
      console.warn('[agencyEncomienda] legacy receipt_url update failed (non-blocking):', legacyErr);
    }
  }

  const count = await contarRecibos(pkgId);
  return { lastUrl, count, errores };
}

export async function subirFotoPaquete({ pkgId, encomiendaPublicId, packageNumber, file } = {}) {
  if (!file) throw fallar('No se recibió ninguna foto.', { __es: true });
  if (file.size > MAX_ARCHIVO_BYTES) throw fallar('La foto no puede exceder 5MB', { __es: true });
  return guard('No se pudo subir la foto del paquete.', async () => {
    const ext = file.name.split('.').pop();
    const fileName = `${encomiendaPublicId}-PK${packageNumber}-photo-${Date.now()}.${ext}`;
    await supabase.storage.from('package-photos').upload(fileName, file);
    const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(fileName);
    const { error } = await supabase
      .from('encomienda_packages')
      .update({ photo_url: urlData.publicUrl })
      .eq('id', pkgId);
    if (error) throw error;
    return urlData.publicUrl;
  });
}

// ── Catálogo ──────────────────────────────────────────────────────────────────

// Búsqueda por fila con unit_type incluido (rankear de catalogSearch no lo
// trae): misma consulta .or() de un ILIKE por token, ranking en cliente.
export async function buscarCatalogoFila(descripcion, { limit = 5 } = {}) {
  const { interpretarDescripcion, rankear } = await import('../../utils/catalogSearch');
  const { tokens, tier } = interpretarDescripcion(descripcion);
  if (tokens.length === 0) return [];
  const or = tokens
    .map((t) => t.replace(/[%_]/g, ''))
    .filter(Boolean)
    .map((t) => `descripcion.ilike.%${t}%`)
    .join(',');
  try {
    const { data, error } = await supabase
      .from('customs_catalog')
      .select('id, descripcion, unidad_medida, unit_type, fob_usd, cip_usd')
      .or(or)
      .limit(25);
    if (error) throw error;
    const porId = new Map((data || []).map((r) => [r.id, r]));
    return rankear(data || [], tokens, tier, { limit }).map((r) => ({
      id: r.id,
      descripcion: r.descripcion,
      unidad_medida: r.unidad_medida ?? null,
      unit_type: porId.get(r.id)?.unit_type ?? null,
      fob_usd: r.fob_usd ?? null,
      cip_usd: r.cip_usd ?? null,
    }));
  } catch (err) {
    console.error('[agencyEncomienda] buscarCatalogoFila', err);
    return [];
  }
}

// La fila de encomienda, tal cual — sin tasas por defecto. null bloquea.
export async function cargarCustomsConfig() {
  return fetchCustomsConfigRow(supabase, 'encomienda');
}
